'use client'

import { type ReactNode, useMemo, useState } from 'react'

import { ButtonRadioGroup } from '@/components/common/Button'
import { LinkRoute } from '@/components/common/Link'
import { ScrollOverflow } from '@/components/common/ScrollOverflow'
import { Subheader } from '@/components/common/Subheader'
import { TableColumnHeader, type TableSortActionType } from '@/components/common/Table'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { usePage } from '@/components/providers/PageProvider'
import { DATEFORMAT_SYSTEM } from '@/defs/date'
import { type IconType } from '@/defs/icon'
import { CARDTYPE_LABELS, type CardtypeType } from '@/defs/mtg'
import { type PageType } from '@/defs/page'
import { usePagination } from '@/hooks/usePagination'
import { cn } from '@/utils/commonUtil'
import { formatDate, formatFullNumber, formatPrice, formatSalt, getTimeFromDate } from '@/utils/formatUtil'
import { buildRoute } from '@/utils/routeUtil'

import styles from './Decks.module.scss'

const COLUMN_SLUGS = [
  'savedate',
  'link',
  'price',
  'salt',
  'creatures',
  'instants',
  'sorceries',
  'artifacts',
  'enchantments',
  'battles',
  'planeswalkers',
  'lands',
  'tags',
] as const
type ColumnSlugType = (typeof COLUMN_SLUGS)[number]

type DeckType = NonNullable<PageType['decks']>[number]

type ConfigType = {
  icon: IconType
  label: string
  render: ({ deck }: { deck: NonNullable<PageType['decks']>[number] }) => ReactNode
  shouldShortenLabel?: boolean
  sortbyFn?: (a: DeckType, b: DeckType) => number
}

const cardtypeColumn = (cardtype: CardtypeType & keyof DeckType): ConfigType => ({
  icon: CARDTYPE_LABELS[cardtype].icon,
  label: CARDTYPE_LABELS[cardtype].label,
  render: ({ deck }) => (deck[cardtype] !== undefined ? formatFullNumber(deck[cardtype]) : 'N/A'),
  shouldShortenLabel: true,
  sortbyFn: (a, b) => (b[cardtype] || 0) - (a[cardtype] || 0),
})

const CONFIG: Record<ColumnSlugType, ConfigType> = {
  artifacts: cardtypeColumn('artifacts'),
  battles: cardtypeColumn('battles'),
  creatures: cardtypeColumn('creatures'),
  enchantments: cardtypeColumn('enchantments'),
  instants: cardtypeColumn('instants'),
  lands: cardtypeColumn('lands'),
  link: {
    icon: 'link',
    label: 'Link',
    render: ({ deck }) => (
      <LinkRoute
        className={styles.link}
        href={buildRoute({ deckpreviewSlug: deck.urlhash, section: 'deckpreview' })}
        newTab
      >
        View Decklist
      </LinkRoute>
    ),
  },
  planeswalkers: cardtypeColumn('planeswalkers'),
  price: {
    icon: 'dollarSign',
    label: 'Price',
    render: ({ deck: { price } }) => (price !== undefined ? formatPrice(price) : 'N/A'),
    sortbyFn: (a, b) => (b.price || 0) - (a.price || 0),
  },
  salt: {
    icon: 'salt',
    label: 'Salt',
    render: ({ deck: { salt } }) => (salt !== undefined ? formatSalt(salt) : ''),
    shouldShortenLabel: true,
    sortbyFn: (a, b) => (b.salt || 0) - (a.salt || 0),
  },
  savedate: {
    icon: 'calendar',
    label: 'Save Date',
    render: ({ deck: { savedate } }) => (savedate !== undefined ? formatDate(savedate, DATEFORMAT_SYSTEM) : 'N/A'),
    sortbyFn: (a, b) => (b.savedate ? getTimeFromDate(b.savedate) : 0) - (a.savedate ? getTimeFromDate(a.savedate) : 0),
  },
  sorceries: cardtypeColumn('sorceries'),
  tags: {
    icon: 'tag',
    label: 'Tags',
    render: ({ deck: { tags } }) => (tags && tags.length > 0 ? tags.join(', ') : ''),
  },
}

const SORTBY_DEFAULT = 'savedate'

export const Decks = () => {
  const { page } = usePage()
  const decksRaw = page?.decks
  const [perPage, setPerPage] = useState(50)
  const [pageNumber, setPageNumber] = useState(1)
  const [sortby, setSortby] = useState<ColumnSlugType>(SORTBY_DEFAULT)
  const [sortbyDirection, setSortbyDirection] = useState<'asc' | 'desc'>('desc')
  const totalPages = Math.ceil((decksRaw?.length || 0) / perPage)
  const paginationOptions = usePagination({ pageNumber, totalPages })

  const decks = useMemo(() => {
    const { sortbyFn } = CONFIG[sortby]

    const decks = !sortbyFn ? decksRaw?.slice() : decksRaw?.slice().sort(sortbyFn)

    if (sortbyDirection === 'asc') decks?.reverse()

    return decks
  }, [decksRaw, sortby, sortbyDirection])

  const perPageOptions = useMemo(
    () =>
      [10, 25, 50, 100].map((o) => ({
        isSelected: perPage === o,
        label: String(o),
        title: `${o} decks per page`,
        value: o,
      })),
    [perPage],
  )

  if (!decks) return null

  const start = formatFullNumber((pageNumber - 1) * perPage + 1)
  const end = formatFullNumber(Math.min(pageNumber * perPage, decks.length))
  const total = formatFullNumber(decks.length)
  const pageLabel = (
    <>
      Showing <strong>{start}</strong> to <strong>{end}</strong> of <strong>{total}</strong> decks
    </>
  )

  const onClickSort = (column: ColumnSlugType, action: TableSortActionType) => {
    setPageNumber(1)

    switch (action) {
      case 'apply': {
        setSortbyDirection('desc')
        setSortby(column)
        break
      }
      case 'reverse': {
        setSortbyDirection('asc')
        break
      }
      case 'reset': {
        setSortbyDirection('desc')
        setSortby(SORTBY_DEFAULT)
        break
      }
    }
  }

  const onSelectPerPage = (num: number) => {
    setPerPage(num)
    setPageNumber(1)
  }

  const onSelectPageNumber = (num: number) => {
    setPageNumber(Math.max(1, Math.min(totalPages, num)))
  }

  return (
    <div className={styles.container}>
      <Subheader>
        <IconWithLabel iconBefore='cards'>Decks</IconWithLabel>
      </Subheader>
      <div className={styles.scrollContainer}>
        <ScrollOverflow>
          <table className={styles.table}>
            <thead>
              <tr>
                {COLUMN_SLUGS.map((column) => {
                  const { icon, label, shouldShortenLabel, sortbyFn } = CONFIG[column]

                  return (
                    <th className={cn(styles.heading, { styles, variant: column })} key={column}>
                      <TableColumnHeader
                        icon={icon}
                        isSortable={!!sortbyFn}
                        isSortby={sortby === column}
                        label={label}
                        onClick={sortbyFn ? (action) => onClickSort(column, action) : undefined}
                        shouldShortenLabel={shouldShortenLabel}
                        sortbyDirection={sortbyDirection}
                      />
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {decks.slice((pageNumber - 1) * perPage, pageNumber * perPage).map((deck, i) => (
                <tr key={deck.urlhash || i}>
                  {COLUMN_SLUGS.map((column) => (
                    <td className={cn(styles.cell, { styles, variant: column })} key={column}>
                      <p>{CONFIG[column].render({ deck })}</p>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollOverflow>
      </div>
      <div className={styles.controls}>
        <p>{pageLabel}</p>
        {totalPages > 1 && (
          <div className={styles.input}>
            <ButtonRadioGroup as='button' onSelect={onSelectPageNumber} options={paginationOptions} />
          </div>
        )}
        <div className={styles.input}>
          <div className={styles.label}>Show per page:</div>
          <ButtonRadioGroup as='button' onSelect={onSelectPerPage} options={perPageOptions} />
        </div>
      </div>
    </div>
  )
}
